/**
 * Unified Diagram Controls - Keyboard Shortcuts Hook
 * Issue #245: Standardize Diagram Controls Across All Renderers
 *
 * Provides consistent keyboard shortcuts across all diagram renderers.
 */

import { useEffect, useCallback } from 'react';
import { useDiagramControlsOptional } from './DiagramControlsContext';

/**
 * Keyboard shortcut definitions
 *
 * Note: We avoid common browser shortcuts (Cmd+L, Cmd+F, Cmd+M, Cmd+G)
 * to prevent conflicts. Using Shift modifier for most shortcuts.
 */
export const KEYBOARD_SHORTCUTS = {
  // View controls - Cmd+Shift to avoid browser conflicts
  FIT_VIEW: { key: '0', ctrl: true, shift: true, description: 'Fit diagram to view' },
  ZOOM_IN: { key: '=', ctrl: true, description: 'Zoom in' },
  ZOOM_OUT: { key: '-', ctrl: true, description: 'Zoom out' },

  // Layout - Cmd+Shift+L to avoid URL bar (Cmd+L)
  AUTO_LAYOUT: { key: 'l', ctrl: true, shift: true, description: 'Apply auto layout' },

  // Search - Cmd+/ as common find shortcut alternative
  SEARCH: { key: '/', ctrl: true, description: 'Focus search input' },
  CLEAR_SEARCH: { key: 'Escape', description: 'Clear search / Exit focus mode' },

  // Focus mode - Cmd+Shift+G to avoid find next (Cmd+G)
  TOGGLE_FOCUS: { key: 'g', ctrl: true, shift: true, description: 'Toggle focus mode' },

  // Filters - unchanged (already uses Shift)
  CLEAR_FILTERS: { key: 'x', ctrl: true, shift: true, description: 'Clear all filters' },

} as const;

/**
 * Options for the keyboard shortcuts hook
 */
export interface UseKeyboardShortcutsOptions {
  /** Whether the shortcuts are enabled */
  enabled?: boolean;
  /** Custom handler for search focus (returns the search input element) */
  onSearchFocus?: () => HTMLInputElement | null;
  /** Whether to prevent default browser behavior */
  preventDefault?: boolean;
}

/**
 * Hook to handle keyboard shortcuts for diagram controls
 *
 * Usage:
 * ```tsx
 * useKeyboardShortcuts({
 *   enabled: true,
 *   onSearchFocus: () => searchInputRef.current,
 * });
 * ```
 */
export function useKeyboardShortcuts(options: UseKeyboardShortcutsOptions = {}) {
  const {
    enabled = true,
    onSearchFocus,
    preventDefault = true,
  } = options;

  const controls = useDiagramControlsOptional();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled || !controls) return;

      // Don't handle shortcuts when typing in input/textarea (except Escape)
      const target = event.target as HTMLElement;
      const isInputElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Check for modifiers
      const hasCtrl = event.ctrlKey || event.metaKey;
      const hasShift = event.shiftKey;
      const key = event.key.toLowerCase();

      // Handle Escape specially (always works)
      if (event.key === 'Escape') {
        if (controls.searchQuery) {
          controls.setSearchQuery('');
          if (preventDefault) event.preventDefault();
        } else if (controls.isFocusMode) {
          controls.toggleFocusMode();
          if (preventDefault) event.preventDefault();
        }
        return;
      }

      // Don't process other shortcuts if in an input
      if (isInputElement) return;

      const { capabilities } = controls;

      // Fit view: Ctrl+Shift+0
      if (hasCtrl && hasShift && key === '0') {
        controls.fitView();
        if (preventDefault) event.preventDefault();
        return;
      }

      // Zoom in: Ctrl+=
      if (hasCtrl && (key === '=' || key === '+')) {
        controls.zoomIn();
        if (preventDefault) event.preventDefault();
        return;
      }

      // Zoom out: Ctrl+-
      if (hasCtrl && key === '-') {
        controls.zoomOut();
        if (preventDefault) event.preventDefault();
        return;
      }

      // Auto layout: Ctrl+Shift+L (avoids Cmd+L for URL bar)
      if (hasCtrl && hasShift && key === 'l' && capabilities.autoLayout) {
        controls.applyAutoLayout();
        if (preventDefault) event.preventDefault();
        return;
      }

      // Search: Ctrl+/ (avoids Cmd+F for browser find)
      if (hasCtrl && key === '/' && capabilities.search) {
        if (onSearchFocus) {
          const input = onSearchFocus();
          input?.focus();
        }
        if (preventDefault) event.preventDefault();
        return;
      }

      // Toggle focus mode: Ctrl+Shift+G (avoids Cmd+G for find next)
      if (hasCtrl && hasShift && key === 'g' && capabilities.focus) {
        controls.toggleFocusMode();
        if (preventDefault) event.preventDefault();
        return;
      }

      // Clear all filters: Ctrl+Shift+X (unchanged)
      if (hasCtrl && hasShift && key === 'x' && capabilities.filters.length > 0) {
        controls.clearAllFilters();
        if (preventDefault) event.preventDefault();
        return;
      }

    },
    [enabled, controls, onSearchFocus, preventDefault]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
}

/**
 * Get a formatted string for a keyboard shortcut
 */
export function formatShortcut(shortcut: { key: string; ctrl?: boolean; shift?: boolean }): string {
  const parts: string[] = [];

  // Use Cmd on Mac, Ctrl on other platforms
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);

  if (shortcut.ctrl) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (shortcut.shift) {
    parts.push(isMac ? '⇧' : 'Shift');
  }

  // Format the key nicely
  let keyDisplay = shortcut.key.toUpperCase();
  if (shortcut.key === 'Escape') keyDisplay = 'Esc';
  if (shortcut.key === '=') keyDisplay = '+';

  parts.push(keyDisplay);

  return parts.join(isMac ? '' : '+');
}

/**
 * Get all keyboard shortcuts with formatted strings
 */
export function getFormattedShortcuts() {
  return Object.entries(KEYBOARD_SHORTCUTS).map(([name, shortcut]) => ({
    name,
    shortcut: formatShortcut(shortcut),
    description: shortcut.description,
  }));
}

/**
 * Component that enables keyboard shortcuts for diagram controls.
 * Must be used inside a DiagramControlsProvider.
 *
 * Usage:
 * ```tsx
 * <DiagramControlsProvider ...>
 *   <DiagramKeyboardShortcuts />
 *   {children}
 * </DiagramControlsProvider>
 * ```
 */
export function DiagramKeyboardShortcuts({
  onSearchFocus,
}: { onSearchFocus?: () => HTMLInputElement | null } = {}) {
  useKeyboardShortcuts({
    enabled: true,
    onSearchFocus,
  });
  return null;
}
